import { environment } from '../config/environment';
import { getPetflixAccessToken } from '../config/petflixAuth';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';

const API_BASE_URL = environment.apiBaseUrl;

class CouponService {
  // Validar código de cupón
  async validateCouponCode(code, contestId) {
    try {
      if (!contestId) {
        throw new Error('Contest ID is required to validate coupon code');
      }

      const response = await fetch(`${API_BASE_URL}/couponpasses/validate`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ 
          code,
          contestId 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error validating coupon code');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error validating coupon code:', error);
      throw error;
    }
  }

  // Usar código de cupón (decrementar uses_remaining)
  async useCouponCode(code, contestId, userId = null, userEmail = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/coupons/use`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({ code, contestId, user_id: userId, user_email: userEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error using coupon code');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error using coupon code:', error);
      throw error;
    }
  }

  // Obtener información del pase de cupones
  async getCouponPassInfo(couponPassId) {
    try {
      const response = await fetch(`${API_BASE_URL}/couponpasses/${couponPassId}`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching coupon pass info');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching coupon pass info:', error);
      throw error;
    }
  }

  // Obtener todos los pases de cupones
  async getAllCouponPasses(forceRefresh = false) {
    try {
      // Agregar timestamp para forzar actualización sin caché si es necesario
      const url = forceRefresh 
        ? `${API_BASE_URL}/couponpasses?_t=${Date.now()}`
        : `${API_BASE_URL}/couponpasses`;
      
      const response = await fetch(url, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching coupon passes');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching coupon passes:', error);
      throw error;
    }
  }

  /** Conteos ligeros para el sidebar del dashboard (sin descargar todas las filas). */
  async getInventorySummary() {
    try {
      const response = await fetch(`${API_BASE_URL}/couponpasses/inventory-summary`, {
        headers: getApiHeadersForGet(),
      });

      if (!response.ok) {
        throw new Error('Error fetching inventory summary');
      }

      const data = await response.json();
      return {
        couponPassCount: Number(data.couponPassCount) || 0,
        passCodeCount: Number(data.passCodeCount) || 0,
        couponCodeCount: Number(data.couponCodeCount) || 0,
      };
    } catch (error) {
      console.error('Error fetching inventory summary:', error);
      return { couponPassCount: 0, passCodeCount: 0, couponCodeCount: 0 };
    }
  }

  // Obtener todos los códigos de cupón
  async getAllCouponCodes() {
    try {
      const response = await fetch(`${API_BASE_URL}/couponcodes`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching coupon codes');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching coupon codes:', error);
      throw error;
    }
  }

  // Generar códigos de cupón específicos para un concurso
  async generateSpecificCouponCodesForContest(contestId, specificCodes, maxUsesPerCode = 3, individual_qr = false, use_existing_codes = false, tagType = 'standard', owner = null, petflix_qr_integration = false) {
    try {
      // Obtener token de autenticación si se necesita usar QR tags existentes
      const accessToken = use_existing_codes ? getPetflixAccessToken() : null;
      
      const response = await fetch(`${API_BASE_URL}/contests/${contestId}/generate-specific-coupon-codes`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          specificCodes,
          maxUsesPerCode,
          individual_qr,
          use_existing_codes,
          accessToken,
          tagType,
          owner,
          petflix_qr_integration
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error generating specific coupon codes');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating specific coupon codes:', error);
      throw error;
    }
  }

  // Generar códigos de Pass_Codes específicos para un concurso
  async generateSpecificPassCodesForContest(contestId, specificCodes, maxUsesPerCode = 3) {
    try {
      console.log('=== GENERANDO CÓDIGOS DE PASS_CODES ESPECÍFICOS ===');
      console.log('Contest ID:', contestId);
      console.log('Specific Codes:', specificCodes);
      console.log('Max Uses Per Code:', maxUsesPerCode);

      const response = await fetch(`${API_BASE_URL}/contests/${contestId}/generate-specific-pass-codes`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          specificCodes: specificCodes,
          maxUsesPerCode: maxUsesPerCode
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error generating specific pass codes');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating specific pass codes:', error);
      throw error;
    }
  }

  // Obtener todos los Pass_Codes
  async getAllPassCodes() {
    try {
      const response = await fetch(`${API_BASE_URL}/passcodes`, {
        headers: getApiHeadersForGet()
      });
      if (!response.ok) {
        throw new Error('Error fetching pass codes');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching pass codes:', error);
      throw error;
    }
  }

  async getContestTokens(contestIds) {
    try {
      const uniqueIds = [...new Set((contestIds || []).filter(Boolean))];
      if (uniqueIds.length === 0) {
        return {};
      }

      const response = await fetch(`${API_BASE_URL}/contest-tokens?contestIds=${encodeURIComponent(uniqueIds.join(','))}`, {
        headers: getApiHeadersForGet()
      });

      if (!response.ok) {
        throw new Error('Error fetching contest tokens');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching contest tokens:', error);
      throw error;
    }
  }

  async rotateContestToken(contestId) {
    try {
      const response = await fetch(`${API_BASE_URL}/contests/${contestId}/token/rotate`, {
        method: 'POST',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error rotating contest token');
      }

      return await response.json();
    } catch (error) {
      console.error('Error rotating contest token:', error);
      throw error;
    }
  }

  // Obtener y usar un pase disponible para un concurso
  async getAndUsePassCode(contestId) {
    try {
      const response = await fetch(`${API_BASE_URL}/passcodes/use/${contestId}`, {
        method: 'POST',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error getting pass code');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting pass code:', error);
      throw error;
    }
  }

  // Eliminar todos los coupon passes de un concurso
  async deleteCouponPassesByContest(contestId) {
    try {
      const response = await fetch(`${API_BASE_URL}/couponpasses/contest/${contestId}`, {
        method: 'DELETE',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting coupon passes by contest');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting coupon passes by contest:', error);
      throw error;
    }
  }

  // Contar coupon passes sin QR tag asignado
  async countCouponPassesWithoutQR(contestId, individual_qr = false) {
    try {
      // Convertir el booleano a string 'true' o 'false' para la URL
      const individual_qr_param = individual_qr ? 'true' : 'false';
      const url = `${API_BASE_URL}/contests/${contestId}/count-coupon-passes-without-qr?individual_qr=${individual_qr_param}`;
      
      console.log('=== CONTANDO COUPON PASSES SIN QR TAG ===');
      console.log('URL:', url);
      console.log('Contest ID:', contestId);
      console.log('Individual QR:', individual_qr);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getApiHeadersForGet(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error counting coupon passes without QR tag');
      }

      const data = await response.json();
      console.log('=== RESPUESTA DEL SERVIDOR ===');
      console.log('Datos recibidos:', JSON.stringify(data, null, 2));
      console.log('==============================\n');
      return data;
    } catch (error) {
      console.error('Error counting coupon passes without QR tag:', error);
      throw error;
    }
  }

  // Asignar QR tags a coupon passes existentes sin QR tag
  async assignQRToExistingCouponPasses(contestId, individual_qr = false, use_existing_codes = false, tagType = 'standard', owner = null, petflix_qr_integration = false) {
    try {
      // Obtener token de autenticación si se necesita usar QR tags existentes
      const accessToken = use_existing_codes ? getPetflixAccessToken() : null;
      
      const requestData = {
        individual_qr,
        use_existing_codes,
        accessToken,
        tagType,
        owner,
        petflix_qr_integration
      };
      
      console.log('\n=== ENVIANDO PETICIÓN A SERVIDOR ===');
      console.log('URL:', `${API_BASE_URL}/contests/${contestId}/assign-qr-to-existing-coupon-passes`);
      console.log('Datos enviados:', JSON.stringify(requestData, null, 2));
      console.log('=====================================\n');
      
      const response = await fetch(`${API_BASE_URL}/contests/${contestId}/assign-qr-to-existing-coupon-passes`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error en la respuesta del servidor:', errorData);
        throw new Error(errorData.error || 'Error assigning QR tags to existing coupon passes');
      }

      const data = await response.json();
      console.log('\n=== RESPUESTA DEL SERVIDOR ===');
      console.log('Datos recibidos:', JSON.stringify(data, null, 2));
      console.log('==============================\n');
      return data;
    } catch (error) {
      console.error('Error assigning QR tags to existing coupon passes:', error);
      throw error;
    }
  }
}

export default new CouponService();